import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import { 
  MessageCircle, 
  Phone, 
  Mail, 
  Clock,
  CheckCircle2,
  AlertCircle,
  Send,
  Search,
  HelpCircle,
  BookOpen,
  Video,
  FileText,
  Zap
} from 'lucide-react';

const CustomerSupport = () => {
  const { toast } = useToast();
  const [supportForm, setSupportForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    priority: 'Medium'
  });
  const [searchTerm, setSearchTerm] = useState('');

  // Mock FAQ data
  const faqs = [
    {
      id: 1,
      question: "How do I reset my password?",
      answer: "Go to the login page and click 'Forgot Password'. Enter your email address and follow the instructions sent to your email.",
      category: "Account"
    },
    {
      id: 2,
      question: "How can I import my contacts?",
      answer: "Navigate to Contacts > Import and upload your CSV file. Make sure your file follows our template format.",
      category: "Data Management"
    },
    {
      id: 3,
      question: "What are the different subscription plans?",
      answer: "We offer Starter, Professional, and Enterprise plans. Each plan includes different features and user limits.",
      category: "Billing"
    },
    {
      id: 4,
      question: "How do I add team members?",
      answer: "Go to Settings > Team Members and click 'Invite User'. Enter their email address and select their role.",
      category: "Team Management"
    },
    {
      id: 5,
      question: "Can I export my data?",
      answer: "Yes, you can export your data from each module using the Export button in the top right corner.",
      category: "Data Management"
    }
  ];

  const quickLinks = [
    {
      title: "Getting Started Guide",
      description: "Learn the basics of using BSQUARE CRM",
      icon: BookOpen,
      color: "bg-blue-100 text-blue-600"
    },
    {
      title: "Video Tutorials",
      description: "Watch step-by-step video guides",
      icon: Video,
      color: "bg-green-100 text-green-600"
    },
    {
      title: "API Documentation",
      description: "Integrate BSQUARE with your tools",
      icon: FileText,
      color: "bg-purple-100 text-purple-600"
    },
    {
      title: "Feature Updates",
      description: "Stay updated with latest features",
      icon: Zap,
      color: "bg-orange-100 text-orange-600"
    }
  ];

  const filteredFAQs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!supportForm.name || !supportForm.email || !supportForm.subject || !supportForm.message) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    // Simulate form submission
    toast({
      title: "Support Request Submitted!",
      description: "We'll get back to you within 24 hours. Ticket ID: TKT-" + Math.random().toString(36).substr(2, 6).toUpperCase(),
    });

    // Reset form
    setSupportForm({
      name: '',
      email: '',
      subject: '',
      message: '',
      priority: 'Medium'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-xl">B²</span>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">BSQUARE Support</h1>
                <p className="text-gray-600">We're here to help you succeed</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <Clock className="h-4 w-4" />
                <span>24/7 Support Available</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Contact Options */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Card className="hover:shadow-lg transition-shadow duration-200">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Live Chat</h3>
              <p className="text-gray-600 text-sm mb-4">Get instant help from our support team</p>
              <Button className="w-full bg-blue-600 hover:bg-blue-700">
                Start Chat
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow duration-200">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Phone className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Phone Support</h3>
              <p className="text-gray-600 text-sm mb-4">Call us for immediate assistance</p>
              <Button variant="outline" className="w-full">
                +1 (555) 123-4567
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow duration-200">
            <CardContent className="p-6 text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <Mail className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="font-semibold text-lg text-gray-900 mb-2">Email Support</h3>
              <p className="text-gray-600 text-sm mb-4">Send us your questions anytime</p>
              <Button variant="outline" className="w-full">
                support@bsquare.com
              </Button>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Support Form */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <MessageCircle className="h-5 w-5 mr-2" />
                  Submit a Support Request
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name *
                      </label>
                      <Input
                        placeholder="Your full name"
                        value={supportForm.name}
                        onChange={(e) => setSupportForm({...supportForm, name: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email *
                      </label>
                      <Input
                        type="email"
                        placeholder="your.email@company.com"
                        value={supportForm.email}
                        onChange={(e) => setSupportForm({...supportForm, email: e.target.value})}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority
                    </label>
                    <select 
                      className="w-full p-2 border border-gray-300 rounded-md"
                      value={supportForm.priority}
                      onChange={(e) => setSupportForm({...supportForm, priority: e.target.value})}
                    >
                      <option value="Low">Low - General inquiry</option>
                      <option value="Medium">Medium - Need assistance</option>
                      <option value="High">High - Urgent issue</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subject *
                    </label>
                    <Input
                      placeholder="Brief description of your issue"
                      value={supportForm.subject}
                      onChange={(e) => setSupportForm({...supportForm, subject: e.target.value})}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Message *
                    </label>
                    <Textarea
                      placeholder="Please describe your issue in detail..."
                      rows={4}
                      value={supportForm.message}
                      onChange={(e) => setSupportForm({...supportForm, message: e.target.value})}
                      required
                    />
                  </div>

                  <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">
                    <Send className="h-4 w-4 mr-2" />
                    Submit Support Request
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Quick Links */}
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Links</h3>
              <div className="grid grid-cols-2 gap-4">
                {quickLinks.map((link, index) => {
                  const Icon = link.icon;
                  return (
                    <Card key={index} className="hover:shadow-md transition-shadow duration-200 cursor-pointer">
                      <CardContent className="p-4">
                        <div className="flex items-center space-x-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${link.color}`}>
                            <Icon className="h-5 w-5" />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900 text-sm">{link.title}</h4>
                            <p className="text-xs text-gray-600 mt-1">{link.description}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <HelpCircle className="h-5 w-5 mr-2" />
                  Frequently Asked Questions
                </CardTitle>
                <div className="mt-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <Input
                      type="search"
                      placeholder="Search FAQs..."
                      className="pl-10"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="max-h-96 overflow-y-auto">
                <div className="space-y-4">
                  {filteredFAQs.map((faq) => (
                    <div key={faq.id} className="border-b border-gray-200 pb-4 last:border-b-0">
                      <div className="flex items-start space-x-3">
                        <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mt-1">
                          <HelpCircle className="h-3 w-3 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 mb-1">{faq.question}</h4>
                          <p className="text-sm text-gray-600 mb-2">{faq.answer}</p>
                          <Badge variant="outline" className="text-xs">
                            {faq.category}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {filteredFAQs.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <HelpCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No FAQs found matching your search.</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Status Indicators */}
            <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <span className="font-medium text-green-900">All Systems Operational</span>
              </div>
              <div className="space-y-1 text-sm text-green-700">
                <div className="flex items-center justify-between">
                  <span>API Services</span>
                  <span className="font-medium">100% Uptime</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Database</span>
                  <span className="font-medium">Operational</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Support Response</span>
                  <span className="font-medium">&lt; 2 hours</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerSupport;